import { get, post } from './httpClient';
import { spaceOwnerQueryParams } from './spaceMembersService';

/**
 * Lotes catastrales (la capa pública de parcelas, no los lotes propios del usuario,
 * que viven en `userLotsService`).
 *
 * El JWT es opcional en el detalle y las UF: responden sin sesión, y con
 * sesión agregan cuota. `getLotViewQuota` sí exige sesión.
 */

const LOT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** ¿Es un `lots.id` válido (UUID)? */
export const isLotUuid = (lotId) => LOT_UUID_RE.test(String(lotId ?? '').trim());

/**
 * Detalle catastral de un lote por id. Id vacío o no UUID → 400 local, sin red.
 *
 * @param {string} lotId - UUID catastral.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {number} [options.retryOn429=0] - Reintentos ante rate limit.
 * @param {boolean} [options.auth=true] - `false` omite el JWT (geometría pública,
 *   no consume la cuota diaria de detalle).
 * @returns {Promise<object>} Envelope de `httpClient`; el lote viene en `data`.
 */
export const getLotById = (lotId, { signal, retryOn429 = 0, auth = true } = {}) => {
  // `lots.id` es UUID: un id vacío o inválido daba 500 en la API (no se llama).
  if (!isLotUuid(lotId)) {
    return Promise.resolve({ _httpOk: false, _httpStatus: 400, error: 'Id de lote inválido.' });
  }
  return get('/getalllots', { params: { Id: String(lotId).trim() }, signal, retryOn429, auth });
};

/**
 * Snapshot del cupo diario de detalle catastral. No consume una consulta.
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>} Envelope de `httpClient`; la cuota viene en `data`.
 * @see docs/maps/lot-view-quota.md
 */
export const getLotViewQuota = ({ signal } = {}) =>
  get('/getlotviewquota', { signal });

/**
 * Igual que `getLotById`, pero desenvuelve el resultado y absorbe los fallos.
 *
 * Es la variante que usan las vistas: el detalle catastral es complementario
 * (planimetría, zonificación), así que si falla se degrada sin romper la pantalla.
 * Reintenta una vez ante 429 porque los prefetch en lote llegan a tocar el límite.
 *
 * @param {string} lotId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {number} [options.retryOn429=1]
 * @param {boolean} [options.auth=true]
 * @returns {Promise<object|null>} El lote, o `null` si no se pudo obtener.
 */
export const getLotDetail = async (lotId, { signal, retryOn429 = 1, auth = true } = {}) => {
  if (!lotId) return null;
  try {
    const result = await getLotById(lotId, { signal, retryOn429, auth });
    return result._httpOk ? result.data ?? null : null;
  } catch {
    return null;
  }
};

/**
 * UF catastrales de Córdoba (Rentas). Ruta pública: sin JWT `user_lot_id` va
 * siempre `null`; con sesión, cada UF trae el overlay de **este** usuario.
 *
 * No va en el GET del lote ni en los tiles: un PH puede tener 142 UF.
 *
 * Espacio ajeno: con `ownerUserId` → `?owner_user_id=`; `user_lot_id` viene de
 * las UF del **owner** (null = el owner no la cargó). No miembro → 403.
 *
 * @param {string} lotId - UUID de `lots.id`.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {string|null} [options.ownerUserId] - Owner del espacio activo (null = propio).
 * @returns {Promise<object>} Envelope de `httpClient`; `data.units` es el listado.
 */
export const getLotFunctionalUnits = (lotId, { signal, ownerUserId } = {}) =>
  get('/getlotfunctionalunits', {
    params: { lot_id: lotId, ...spaceOwnerQueryParams(ownerUserId) },
    signal,
  });

/**
 * Como `getLotFunctionalUnitsList`, pero conserva el status HTTP del error
 * (p. ej. 403 del invitado sin acceso) para que la UI pueda avisar.
 *
 * @param {string} lotId
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 * @returns {Promise<{ units: object[], errorStatus: number|null }>}
 */
export const fetchLotFunctionalUnits = async (lotId, { signal, ownerUserId } = {}) => {
  if (!lotId) return { units: [], errorStatus: null };
  try {
    const result = await getLotFunctionalUnits(lotId, { signal, ownerUserId });
    if (!result?._httpOk) {
      return { units: [], errorStatus: result?._httpStatus ?? null };
    }
    const units = result?.data?.units;
    return { units: Array.isArray(units) ? units : [], errorStatus: null };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('Error fetching lot functional units:', error);
    return { units: [], errorStatus: null };
  }
};

/**
 * Desenvuelve `getLotFunctionalUnits`. Ante error HTTP o de red (salvo abort)
 * devuelve `[]` para no romper el tab Unidades: el flujo de UF propias sigue.
 *
 * @param {string} lotId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object[]>}
 */
export const getLotFunctionalUnitsList = async (lotId, { signal, ownerUserId } = {}) => {
  if (!lotId) return [];
  try {
    const result = await getLotFunctionalUnits(lotId, { signal, ownerUserId });
    if (!result?._httpOk) return [];
    const units = result?.data?.units;
    return Array.isArray(units) ? units : [];
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('Error fetching lot functional units:', error);
    return [];
  }
};

/**
 * Recorte de mapa por UF catastrales. Devuelve **lotes padre** (alguna UF
 * cumple). No reemplaza `getLotFunctionalUnits`: ese GET sigue siendo el
 * detalle de un lote.
 *
 * @param {object} payload
 * @param {number[]} payload.bbox - `[west, south, east, north]` de la vista.
 * @param {string} [payload.province]
 * @param {object} [payload.filters]
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>} Envelope `_httpOk`; `data.lot_ids`, `data.lots`
 *   (`{ id, lat, lng }`, mismo orden/tope), `match_count`, `unit_count`,
 *   `truncated`.
 * @see docs/maps/official-unit-filters.md
 */
export const filterOfficialUnits = (payload, { signal } = {}) =>
  post('/filterofficialunits', { body: payload, signal });

